import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  getAllTreatmentPlansWithMeta,
  TreatmentPlanPaginationPayload,
  deleteTreatmentPlan,
  TreatmentPlan,
} from "../../api/treatmentPlan";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";

const defaultPayload: TreatmentPlanPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "createdOn",
  direction: "desc",
  whereClause: [],
};

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const TreatmentPlanList: React.FC = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] =
    useState<TreatmentPlanPaginationPayload>(defaultPayload);
  const [search, setSearch] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const lastFetchKeyRef = useRef<string>("");

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const body: TreatmentPlanPaginationPayload = {
        ...payload,
        whereClause: search
          ? [
            {
              key: "patientName",
              value: search,
              operator: "=",
            },
          ]
          : [],
      };

      const res = await getAllTreatmentPlansWithMeta(body);
      const list = Array.isArray(res.data) ? res.data : [];
      setPlans(list);
      setTotal(res.total || res.count || list.length || 0);
    } catch (err) {
      setPlans([]);
      setTotal(0);
      toast.error("Failed to load treatment plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = JSON.stringify({
      p: payload.curPage,
      pp: payload.perPage,
      s: payload.sortBy,
      d: payload.direction,
      q: search,
    });
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.curPage, payload.perPage, payload.sortBy, payload.direction, search]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');

      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const requestDelete = (id: string) => {
    setSelectedId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await deleteTreatmentPlan(selectedId);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchPlans();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete plan.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Treatment Plans
                </h2>
                <div className="flex flex-col md:flex-row md:items-center md:gap-4">

                  <button
                    className="mt-3 md:mt-0 px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                    onClick={() => navigate("/treatment-plan/create")}
                  >
                    + New Treatment Plan
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  Total: <span className="font-medium">{total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-full md:w-64">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPayload((prev) => ({ ...prev, curPage: 1 }));
                      }}
                      placeholder="Search by patient name..."
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={changePerPage}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Patient Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Treatment Type
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Created On
                      </th>

                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={4}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      plans.map((plan) => (
                        <tr key={plan.id} className="border-t">

                          <td className="px-4 py-3 text-sm text-gray-600">
                            {plan.patientName ?? "N/A"}

                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {plan.type || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {plan.createdAt ? formatDate(plan.createdAt) : "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div
                              className="relative"
                              ref={(el) => {
                                if (openMenuId === plan.id) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    buttonRefs.current[plan.id] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  if (openMenuId === plan.id) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      right: window.innerWidth - rect.right,
                                    });
                                    setOpenMenuId(plan.id);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === plan.id && dropdownPosition && createPortal(
                                <div
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    right: `${dropdownPosition.right}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      navigate(`/treatment-plan/create/${plan.id}`);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  {/* <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      requestDelete(plan.id);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button> */}
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <hr />
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Treatment Plan"
                message="Are you sure you want to delete this plan?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />

              <SuccessModal
                isOpen={showSuccess}
                title="Plan Deleted"
                message="The treatment plan has been deleted."
                onClose={() => setShowSuccess(false)}
              />
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default TreatmentPlanList;
